import type { Row, RowData } from '@tanstack/react-table';
import {
  type FocusEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type RefCallback,
  useCallback,
  useRef,
  useState,
} from 'react';
import type { TableRowSelection } from '../Table';
import type { kkTableFeatures } from '../tableFeatures';

type TableRowNavigationKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

export interface TableRowInteractionProps {
  'aria-disabled'?: boolean;
  'aria-selected'?: boolean;
  'data-selectable-row'?: 'true';
  onClick?: MouseEventHandler<HTMLTableRowElement>;
  onFocus?: FocusEventHandler<HTMLTableRowElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTableRowElement>;
  ref: RefCallback<HTMLTableRowElement>;
  tabIndex?: number;
}

interface UseTableRowInteractionsOptions<TData extends RowData> {
  measureElement?: RefCallback<HTMLTableRowElement>;
  onRowClick?: (row: Row<typeof kkTableFeatures, TData>) => void;
  rowSelection?: TableRowSelection<TData>;
  rows: Row<typeof kkTableFeatures, TData>[];
  scrollToIndex?: (index: number) => void;
}

function isNavigationKey(key: string): key is TableRowNavigationKey {
  return (
    key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End'
  );
}

function getNextRowIndex(
  currentIndex: number,
  key: TableRowNavigationKey,
  rowCount: number,
): number {
  if (key === 'Home') return 0;
  if (key === 'End') return rowCount - 1;
  return Math.max(
    0,
    Math.min(rowCount - 1, currentIndex + (key === 'ArrowDown' ? 1 : -1)),
  );
}

function getTabStopRowId<TData extends RowData>(
  rows: Row<typeof kkTableFeatures, TData>[],
  activeRowId: string | null,
): string | undefined {
  let visibleActiveRowId: string | undefined;
  let selectedRowId: string | undefined;

  for (const row of rows) {
    if (row.id === activeRowId) visibleActiveRowId = row.id;
    if (selectedRowId === undefined && row.getIsSelected()) {
      selectedRowId = row.id;
    }
  }

  return visibleActiveRowId ?? selectedRowId ?? rows[0]?.id;
}

export function useTableRowInteractions<TData extends RowData>({
  measureElement,
  onRowClick,
  rowSelection,
  rows,
  scrollToIndex,
}: UseTableRowInteractionsOptions<TData>) {
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const pendingFocusRowId = useRef<string | null>(null);
  const rowElements = useRef(new Map<string, HTMLTableRowElement>());
  const rowRefCallbacks = useRef(
    new Map<string, RefCallback<HTMLTableRowElement>>(),
  );
  const measureElementRef = useRef(measureElement);
  measureElementRef.current = measureElement;

  const tabStopRowId = getTabStopRowId(rows, activeRowId);

  const registerRow = useCallback(
    (rowId: string, element: HTMLTableRowElement | null) => {
      measureElementRef.current?.(element);

      if (!element) {
        rowElements.current.delete(rowId);
        return;
      }

      rowElements.current.set(rowId, element);
      if (pendingFocusRowId.current === rowId) {
        pendingFocusRowId.current = null;
        element.focus();
      }
    },
    [],
  );

  const getRowRef = useCallback(
    (rowId: string) => {
      const existingRef = rowRefCallbacks.current.get(rowId);
      if (existingRef) return existingRef;

      const rowRef: RefCallback<HTMLTableRowElement> = (element) =>
        registerRow(rowId, element);
      rowRefCallbacks.current.set(rowId, rowRef);
      return rowRef;
    },
    [registerRow],
  );

  const focusRow = useCallback(
    (row: Row<typeof kkTableFeatures, TData>, index: number) => {
      pendingFocusRowId.current = row.id;
      setActiveRowId(row.id);
      scrollToIndex?.(index);

      const element = rowElements.current.get(row.id);
      if (element) {
        pendingFocusRowId.current = null;
        element.focus();
      }
    },
    [scrollToIndex],
  );

  const getRowInteractionProps = useCallback(
    (
      row: Row<typeof kkTableFeatures, TData>,
      index: number,
    ): TableRowInteractionProps => {
      const isSelectable = rowSelection !== undefined;
      const isDisabled = rowSelection?.isRowDisabled?.(row) ?? false;
      const activateRow = () => {
        if (isDisabled) return;
        if (isSelectable) setActiveRowId(row.id);
        if (!row.getIsSelected()) rowSelection?.onChange(row);
        onRowClick?.(row);
      };

      return {
        'aria-disabled': isDisabled || undefined,
        'aria-selected': isSelectable ? row.getIsSelected() : undefined,
        'data-selectable-row': isSelectable && !isDisabled ? 'true' : undefined,
        onClick: isSelectable || onRowClick ? activateRow : undefined,
        onFocus: isSelectable
          ? () => {
              setActiveRowId(row.id);
            }
          : undefined,
        onKeyDown: isSelectable
          ? (event) => {
              if (event.target !== event.currentTarget) return;

              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                activateRow();
                return;
              }

              if (!isNavigationKey(event.key)) return;

              const nextIndex = getNextRowIndex(index, event.key, rows.length);
              const nextRow = rows[nextIndex];
              if (!nextRow) return;

              event.preventDefault();
              focusRow(nextRow, nextIndex);
            }
          : undefined,
        ref: getRowRef(row.id),
        tabIndex: isSelectable ? (row.id === tabStopRowId ? 0 : -1) : undefined,
      };
    },
    [focusRow, getRowRef, onRowClick, rowSelection, rows, tabStopRowId],
  );

  return { getRowInteractionProps };
}
